import React, { useEffect, useMemo, useRef, useState } from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import { pickBestItemMatch } from "../../../utils/stock-in-pdf-parser";
import errorToast from "../../../components/helper-components/error-toast";
import { useDispatch } from "react-redux";
import { goodsTempProImpAction } from "../../../redux/actions/purchase-action";
import {
  purchaseProductDetailsApi,
  purchaseProductListApi,
} from "../../../redux/apis/purchase-api";
import { VendorMappingAction } from "../../../redux/actions/settings-action";
import SelectField from "../../../components/forms/select-field";
import ConfirmModal from "../../../components/helper-components/confirm-modal";
import { AlertCircle, CheckCircle2, Trash2 } from "lucide-react";
import { mergeImportedBatches } from "../../../utils/purchase-batch-dedupe";
const GoodsOrderCsv = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state?.data || [];
  const type = location.state?.type || [];
  const prefixName = type === "grn" ? "gort" : "poit";
  const [mismatchInfo, setMismatchInfo] = useState(null);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);
  // Product matching per row, keyed by the row's stable _row id (same flow as
  // the Stock IN "Import PDF"):
  //   state: "searching" | "matched" (exact) | "check" (close match, verify)
  //          | "suggest" (pick from candidates) | "none" | "manual" | "done"
  //   candidates: products from the search, shown as the field's suggestions
  const [rowMatch, setRowMatch] = useState({});
  const formikRef = useRef(null);

  // Built once: rows get a stable _row id so async matching still finds the
  // right row after the user removes rows.
  const initialValue = useMemo(
    () => ({
      ...data,
      itemData: (data?.itemData || []).map((item, index) => ({
        ...autoMatchPacking(item, cell(item, data?.columnMapping?.item_uom)),
        _row: index,
      })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const setMatch = (rowKey, patch) =>
    setRowMatch((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), ...patch },
    }));

  // Write a product (and its packing) onto a row, found by _row id. The
  // packing is the product's purchasing UOM; its full UOM list comes from the
  // product details so the Packing dropdown has every option.
  const linkProduct = async (rowKey, product, state) => {
    const form = formikRef.current;
    const findIndex = () =>
      (form?.values?.itemData || []).findIndex((r) => r?._row === rowKey);
    let index = findIndex();
    if (!form || index === -1 || !product?.item_id) return;
    form.setFieldValue(`itemData[${index}]`, {
      ...form.values.itemData[index],
      item_name: product.item_name,
      item_id: product.item_id,
      item_code: product.item_code,
      is_item_in_db: true,
      item_uom_id: product.item_pur_purchasing_uom_name_id || "",
      item_uom: product.item_pur_purchasing_uom_name || "",
      is_item_uom_in_db: !!product.item_pur_purchasing_uom_name_id,
    });
    setMatch(rowKey, { state });

    const details = await fetchProductDetails(product).catch(() => null);
    index = findIndex();
    const row = form.values.itemData?.[index];
    if (!details || index === -1 || row?.item_id !== product.item_id) return;
    const { rawUomList, matchedUom } = resolveUom(product, details);
    form.setFieldValue(`itemData[${index}]`, {
      ...row,
      uom_dropdown: rawUomList,
      ...(matchedUom
        ? {
            item_uom_id: matchedUom.item_id,
            item_uom: matchedUom.alt_uom,
            is_item_uom_in_db: true,
          }
        : {}),
    });
  };

  // On open: every unlinked row is searched by the vendor's product name
  // (then its first word) and linked to the best match; linked rows without a
  // packing get the product's purchasing UOM. A few lookups at a time.
  useEffect(() => {
    const cm = initialValue?.columnMapping || {};
    const pending = (initialValue?.itemData || []).filter(
      (r) => !r?.item_id || !r?.item_uom_id
    );
    if (!pending.length) return;
    let cancelled = false;
    const cache = {};
    const search = (query) => {
      const key = normalizeName(query);
      if (!cache[key]) cache[key] = searchProducts(query).catch(() => []);
      return cache[key];
    };

    setRowMatch((prev) => {
      const next = { ...prev };
      pending.forEach((r) => (next[r._row] = { state: "searching" }));
      return next;
    });

    const jobs = pending.map((row) => async () => {
      if (row?.item_id) {
        const found = (await search(row?.item_name)).find(
          (p) => p?.item_id === row.item_id
        );
        if (cancelled) return;
        if (found) await linkProduct(row._row, found, "done");
        else setMatch(row._row, { state: "done" });
        return;
      }

      const vendorName = String(
        cell(row, cm.item_name) || row?.item_name || ""
      ).trim();
      const vendorCode = normalizeName(cell(row, cm.item_code));
      const results = await search(vendorName);
      let match =
        pickBestItemMatch(vendorName, results) ||
        (vendorCode &&
        results.filter((p) => normalizeName(p?.item_code) === vendorCode)
          .length === 1
          ? {
              item: results.find(
                (p) => normalizeName(p?.item_code) === vendorCode
              ),
              exact: true,
            }
          : null);
      let candidates = results;
      if (!match) {
        const firstWord = vendorName.split(/\s+/)[0];
        if (firstWord && firstWord !== vendorName && firstWord.length >= 3) {
          const wider = await search(firstWord);
          match = pickBestItemMatch(vendorName, wider);
          if (!results.length) candidates = wider;
        }
      }
      if (cancelled) return;
      setMatch(row._row, { candidates });
      if (match?.item) {
        await linkProduct(row._row, match.item, match.exact ? "matched" : "check");
      } else {
        setMatch(row._row, { state: candidates.length ? "suggest" : "none" });
      }
    });

    runLimited(jobs);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Product search for the Linked product field (called directly - the redux
  // search action is takeLatest and would cancel lookups).
  const loadProductOptions = useMemo(
    () =>
      debounce((input, callback) => {
        searchProducts(input)
          .then((products) => callback(products.map(toProductOption)))
          .catch(() => callback([]));
      }, 400),
    []
  );

  // Picking a product for one row also fills every other unlinked row with
  // the same vendor product (the same item in another batch).
  const handlePickProduct = (row, option, values) => {
    const cm = values?.columnMapping || {};
    const name = normalizeName(cell(row, cm.item_name) || row?.item_name);
    (values?.itemData || [])
      .filter(
        (other) =>
          other._row === row._row ||
          (!other.item_id &&
            normalizeName(cell(other, cm.item_name) || other?.item_name) ===
              name)
      )
      .forEach((other) => linkProduct(other._row, option.product, "manual"));
  };
  const validationSchema = Yup.object().shape({

  });

  // Document-level totals (SubTotal / Invoice Total Amount) are mapped per the
  // vendor's import template, so they sit on every CSV row under the raw header
  // name given in columnMapping — same lookup pattern as item_code/item_name.
  const getMappedDocumentValue = (values, key) => {
    const column = values?.columnMapping?.[key];
    if (!column) return null;
    const parsed = parseFloat(cell(values?.itemData?.[0], column));
    return Number.isNaN(parsed) ? null : parsed;
  };
  // Build the add-vendor-mapping payload from the reviewed rows, grouping every
  // CSV row by its vendor item code so multiple vendor UOMs collapse into one
  // item's mapping list (same shape the vendor-mapping screen sends).
  const buildVendorMappingPayload = (values) => {
    const cm = values?.columnMapping || {};
    const cd = values?.customerDetails || {};
    const grouped = {};

    (values?.itemData || []).forEach((item) => {
      const vendorCode = (cell(item, cm.item_code) ?? "").toString().trim();
      const vendorName = (cell(item, cm.item_name) ?? "").toString();
      // Vendor UOM as it appears in the vendor's CSV column. When the file has
      // no UOM column (e.g. the pack is embedded in the item name), that cell is
      // blank, so fall back to the backend-translated vendor_uom and finally to
      // the resolved/selected system UOM so the mapping is still captured.
      const vendorUom = (
        cell(item, cm.item_uom) ||
        item?.vendor_uom ||
        item?.item_uom ||
        ""
      )
        .toString()
        .trim();
      if (!vendorCode || !item?.item_id) return; // need a resolved system item

      if (!grouped[vendorCode]) {
        grouped[vendorCode] = {
          vendor_item_code: vendorCode,
          vendor_item_name: vendorName,
          system_item_id: item?.item_id || "",
          system_item_code: item?.item_code || "",
          system_item_name: item?.item_name || "",
          mapping: [],
        };
      }

      // Same vendor UOM on several rows (e.g. two batches) -> map it once.
      const alreadyMapped = grouped[vendorCode].mapping.some(
        (m) => normalizeUom(m.vendor_uom) === normalizeUom(vendorUom)
      );
      if (vendorUom && item?.item_uom_id && !alreadyMapped) {
        grouped[vendorCode].mapping.push({
          vendor_uom: vendorUom,
          system_uom: item?.item_uom || "",
          system_uom_id: item?.item_uom_id || "",
          uom_mapping_id: null,
        });
      }
    });

    return {
      vendor_code: cd.cmr_code || "",
      vendor_name: cd.cmr_name || cd.cmr_first_name || cd.cmr_vendor_name || "",
      vendor_id: cd.cmr_id || "",
      data: Object.values(grouped),
    };
  };

  // Finalizes the import: recomputes the system totals from the resolved items,
  // persists vendor -> system mappings, and navigates into the create screen.
  // Pulled out of the dispatch success callback so both the direct path and the
  // "proceed anyway" confirmation (after a total-amount mismatch) can call it.
  const finishImport = (data, values) => {
    // Line amounts come back as raw float sums of the batches (a two-batch row
    // showed tax 22.950000000000003) - keep them at 2 decimals like the batch
    // panel does.
    const round2 = (v) => +(parseFloat(v) || 0).toFixed(2);
    // Same product on several rows -> one row, and lines that are the same
    // batch (same expiry and prices) -> one batch with the quantities added.
    const itemsData = mergeImportedBatches(data?.data?.itemData ?? []).map((item) => ({
      ...item,
      item_tax_amount: round2(item?.item_tax_amount),
      item_total_amount: round2(item?.item_total_amount),
      item_discount_amount: round2(item?.item_discount_amount),
    }));

    const raw_total_amount = itemsData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_total_amount) || 0);
    }, 0);

    const total_tax = itemsData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_tax_amount) || 0);
    }, 0);

    const total_discount = itemsData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_discount_amount) || 0);
    }, 0);

    // Subtotal is the pre-discount, pre-tax amount: Total = Subtotal -
    // Discount + Tax, so Subtotal = Total - Tax + Discount. Same formula the
    // GRN/PI batch panel and footer use, so the imported document's subtotal
    // matches what editing any batch afterwards recomputes.
    const sub_total = (
      raw_total_amount -
      total_tax +
      total_discount
    ).toFixed(2);

    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (
      Math.round(raw_total_amount) - raw_total_amount
    ).toFixed(2);

    // after the GRN import, also persist the vendor -> system item/UOM
    // mappings for these rows via the add-vendor-mapping API
    const vendorMappingPayload = buildVendorMappingPayload(values);
    if (vendorMappingPayload.data.length > 0) {
      dispatch(
        VendorMappingAction(
          vendorMappingPayload,
          () => {},
          (err) =>
            console.error("Vendor mapping failed after GRN import", err)
        )
      );
    }

    const path =
      type === "grn"
        ? `/goods-receipt/${encodeURIComponent(JSON.stringify([]))}/goods`
        : `/add-purchase-invoice/${encodeURIComponent(
            JSON.stringify([])
          )}/invoice`;
    navigate(path, {
      state: {
        values: {
          itemData: itemsData,
          ...values?.customerDetails,
          gst_type:
            data?.data?.orderData?.gst_type ?? values?.customerDetails?.gst_type,
          [`${prefixName}_sub_total`]: sub_total,
          [`${prefixName}_total_gst`]: total_tax.toFixed(2),
          [`${prefixName}_total_discount`]: total_discount.toFixed(2),
          [`${prefixName}_total_amount`]: total_amount,
          [`${prefixName}_round_off_value`]: round_off_value,
          [`${prefixName}_tcs_or_tds_amount`]: 0,
          [`${prefixName}_tcs_or_tds_percentage_id`]: "",
          [`${prefixName}_tcs_or_tds_percentage`]: 0,
          [`${prefixName}_tds_tcs_type`]: "",
        },
      },
    });
  };

  const handleSubmit = (values, actions) => {
    const missingSuggestedItem = values?.itemData?.some(
      (item) => !item.item_id
    );
    const missingBaseUOM = values?.itemData?.some((item) => !item.item_uom_id);
    if (missingSuggestedItem) {
      errorToast("Linked Product Name is required!");
      actions.setSubmitting(false); // Stop the form submission if the validation fails
      return;
    }
    if (missingBaseUOM) {
      errorToast("Base UOM is required!");
      actions.setSubmitting(false); // Stop the form submission if the validation fails
      return;
    }

    const importedTotalAmount = getMappedDocumentValue(values, "total_amount");

    dispatch(
      goodsTempProImpAction(
        {
          total_amount: importedTotalAmount ?? 0,
          itemData: values?.itemData?.map((item) => ({
            item_uom: item?.item_uom,
            item_code: item?.item_code,
            item_name: item?.item_name,
            item_hsn: item?.item_hsn,
            item_exp_date: item?.item_exp_date,
            item_sellable_quantity: item?.item_sellable_quantity,
            item_batch_number: item?.item_batch_number,
            item_generic_name: item?.item_generic_name,
            item_free_quantity: item?.item_free_quantity,
            item_batch_free_quantity: item?.item_batch_free_quantity,
            item_tax_percentage:
              item?.item_tax_percentage ?? gstOf(item).gst,
            item_batch_unit_price: item?.item_batch_unit_price,
            item_batch_purchase_rate: item?.item_batch_purchase_rate,
            item_batch_purhcase_rate_before_tax: item?.item_batch_purhcase_rate_before_tax,
            item_batch_discount_percentage:
              item?.item_batch_discount_percentage,
            item_id: item?.item_id,
            is_item_in_db: item?.is_item_in_db,
            item_uom_id: item?.item_uom_id,
            is_item_uom_in_db: item?.is_item_uom_in_db,
            item_gst: gstOf(item).gst,
            item_cgst_percentage: gstOf(item).cgst,
            item_sgst_percentage: gstOf(item).sgst,
            item_igst_percentage: gstOf(item).igst,
            item_batch_ptr_amount: parseFloat(item?.item_batch_ptr_amount) || 0,
            item_batch_pts_amount: parseFloat(item?.item_batch_pts_amount) || 0,
          })),
        },
        (data) => {
          const itemsData = data?.data?.itemData || [];
          const systemCalculatedTotal = Math.round(
            itemsData.reduce(
              (sum, item) => sum + (parseFloat(item.item_total_amount) || 0),
              0
            )
          );

          if (
            importedTotalAmount !== null &&
            Math.abs(importedTotalAmount - systemCalculatedTotal) > 1
          ) {
            setMismatchInfo({
              importedTotalAmount,
              systemCalculatedTotal,
              columnName: values?.columnMapping?.total_amount,
              onProceed: () => finishImport(data, values),
            });
            return;
          }

          finishImport(data, values);
        },
        onErrorImport
      )
    );
  };

  const onErrorImport = (data) => {
    errorToast(data?.message || data?.data?.message);
  };

  return (
    <InnerLayout
      backBtn={true}
      title={
        prefixName === "gort"
          ? "Review GRN Import"
          : "Review Purchase Invoice Import"
      }
    >
      <Formik
        innerRef={formikRef}
        enableReinitialize={true}
        initialValues={initialValue}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
          const cm = values?.columnMapping || {};
          const rows = values?.itemData || [];
          const issues = rows.map(rowIssues);
          const issueCount = issues.filter((i) => i.length > 0).length;
          const readyCount = rows.length - issueCount;
          const fileTotal = getMappedDocumentValue(values, "total_amount");
          const vendorName =
            values?.customerDetails?.cmr_name ||
            values?.customerDetails?.cmr_first_name ||
            values?.customerDetails?.cmr_vendor_name ||
            "";
          const visible = rows
            .map((item, index) => ({ item, index }))
            .filter(({ index }) => !showIssuesOnly || issues[index].length > 0);

          const removeRow = (index) => {
            setFieldValue(
              "itemData",
              rows.filter((_, i) => i !== index)
            );
          };

          return (
            <Form className="h-full flex flex-col">
              {/* Summary bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-EAECF0">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-[var(--description-color)]">
                  {vendorName && (
                    <span>
                      Vendor{" "}
                      <span className="font-semibold text-[var(--heading-color)]">
                        {vendorName}
                      </span>
                    </span>
                  )}
                  <span>
                    Rows{" "}
                    <span className="font-semibold text-[var(--heading-color)]">
                      {rows.length}
                    </span>
                  </span>
                  {fileTotal !== null && (
                    <span>
                      File total{" "}
                      <span className="font-semibold text-[var(--heading-color)]">
                        ₹{fileTotal.toFixed(2)}
                      </span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`${CHIP} text-[var(--import-button-fg)] bg-[var(--import-button-bg)] border-[var(--import-button-border)]`}
                  >
                    <CheckCircle2 size={14} />
                    {readyCount} ready
                  </span>
                  {(() => {
                    const states = Object.values(rowMatch).map(
                      (m) => m?.state
                    );
                    const count = (s) => states.filter((x) => x === s).length;
                    return (
                      <>
                        {count("searching") > 0 && (
                          <span
                            className={`${CHIP} text-[var(--primary)] bg-[var(--row-hover-color)] border-[var(--import-border)]`}
                          >
                            Matching products… {count("searching")} left
                          </span>
                        )}
                        {count("matched") + count("check") > 0 && (
                          <span
                            className={`${CHIP} text-[var(--primary)] bg-white border-[var(--import-border)]`}
                            title="Linked automatically from the product master — please review"
                          >
                            {count("matched") + count("check")} auto-matched
                          </span>
                        )}
                      </>
                    );
                  })()}
                  {issueCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowIssuesOnly((v) => !v)}
                      className={`${CHIP} ${ERROR_CHIP} ${
                        showIssuesOnly
                          ? "shadow-[0_0_0_1px_var(--error-color)]"
                          : ""
                      }`}
                      title="Show only rows that need attention"
                    >
                      <AlertCircle size={14} />
                      {issueCount} need attention
                    </button>
                  )}
                  {showIssuesOnly && (
                    <button
                      type="button"
                      onClick={() => setShowIssuesOnly(false)}
                      className="text-xs font-semibold text-[var(--primary)] underline px-1"
                    >
                      Show all
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-F2F4F5">
                      <th colSpan={2} className={GROUP_TH} />
                      <th colSpan={3} className={GROUP_TH}>
                        From vendor file
                      </th>
                      <th
                        colSpan={3}
                        className={`${GROUP_TH} border-x border-x-EAECF0`}
                      >
                        Linked in system
                      </th>
                      <th colSpan={8} className={GROUP_TH}>
                        Batch details
                      </th>
                      <th className={GROUP_TH} />
                    </tr>
                    <tr className="bg-F2F4F5">
                      {COLUMNS.map((col, i) => (
                        <th
                          key={col.label + i}
                          className={`${TH} ${
                            col.num ? "text-right" : "text-left"
                          } ${col.className || ""}`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {visible.map(({ item, index }) => {
                      const rowIssue = issues[index];
                      const needsProduct = !item?.item_id;
                      const needsUom = !item?.item_uom_id;
                      return (
                        <tr
                          key={index}
                          className={`border-b border-b-EAECF0 ${
                            rowIssue.length
                              ? "bg-[var(--import-review-issue-row-bg)]"
                              : "hover:bg-f3f8ff"
                          }`}
                        >
                          {/* Status */}
                          <td className={`${TD} w-[2.5rem]`}>
                            {rowIssue.length ? (
                              <span
                                title={rowIssue.join(", ")}
                                className="flex text-[var(--error-color)]"
                              >
                                <AlertCircle size={18} />
                              </span>
                            ) : (
                              <span
                                title="Ready to import"
                                className="flex text-[var(--import-button-fg)]"
                              >
                                <CheckCircle2 size={18} />
                              </span>
                            )}
                          </td>
                          <td
                            className={`${TD} text-[var(--description-color)]`}
                          >
                            {index + 1}
                          </td>

                          {/* From vendor file */}
                          <td className={`${TD} whitespace-nowrap`}>
                            {show(cell(item, cm.item_code))}
                          </td>
                          <td className={`${TD} min-w-[11rem]`}>
                            {show(cell(item, cm.item_name))}
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            {show(cell(item, cm.item_uom))}
                          </td>

                          {/* Linked in system */}
                          <td
                            className={`${TD} whitespace-nowrap border-l border-l-EAECF0`}
                          >
                            {/* The server echoes the vendor's code/name on
                                unlinked rows - only show them once linked. */}
                            {show(item?.item_id ? item?.item_code : "")}
                          </td>
                          <td className={`${TD} min-w-[20rem] py-2`}>
                            {(() => {
                              const m = rowMatch[item._row] || {};
                              const candidates = m.candidates || [];
                              return (
                                <>
                                  <AsyncSelect
                                    value={
                                      item?.item_id
                                        ? {
                                            value: item.item_id,
                                            label: item.item_name,
                                          }
                                        : null
                                    }
                                    hasError={needsProduct}
                                    isClearable
                                    cacheOptions={false}
                                    loadOptions={loadProductOptions}
                                    defaultOptions={candidates.map(
                                      toProductOption
                                    )}
                                    placeholder={
                                      m.state === "searching"
                                        ? "Searching products…"
                                        : candidates.length
                                        ? "Pick a suggestion or search…"
                                        : "Search product…"
                                    }
                                    isLoading={m.state === "searching"}
                                    noOptionsMessage={({ inputValue }) =>
                                      inputValue
                                        ? "No matching products"
                                        : "Type to search products"
                                    }
                                    styles={productSelectStyles}
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    onChange={(option) => {
                                      if (!option) {
                                        // Cleared: unlink, keep the suggestions
                                        setFieldValue(`itemData[${index}]`, {
                                          ...item,
                                          item_id: "",
                                          item_name: "",
                                          item_code: "",
                                          item_uom_id: "",
                                          item_uom: "",
                                        });
                                        setMatch(item._row, {
                                          state: candidates.length
                                            ? "suggest"
                                            : "none",
                                        });
                                        return;
                                      }
                                      handlePickProduct(item, option, values);
                                    }}
                                  />
                                  {(() => {
                                    const line = (cls, text) => (
                                      <div className={`mt-1 text-[11px] ${cls}`}>
                                        {text}
                                      </div>
                                    );
                                    if (m.state === "searching" && needsProduct)
                                      return line(
                                        "text-[var(--primary)]",
                                        "Matching with your products…"
                                      );
                                    if (item?.item_id && m.state === "matched")
                                      return line(
                                        "fw_600 text-[var(--import-button-fg)]",
                                        "✓ Matched"
                                      );
                                    if (item?.item_id && m.state === "check")
                                      return line(
                                        "fw_600 text-[var(--edit-color)]",
                                        "Close match — please verify"
                                      );
                                    if (needsProduct && candidates.length)
                                      return line(
                                        "text-[var(--error-color)]",
                                        `No exact match — ${candidates.length} suggestion${
                                          candidates.length > 1 ? "s" : ""
                                        }, click to pick`
                                      );
                                    if (needsProduct && m.state === "none")
                                      return (
                                        <div className="mt-1 text-[11px] text-[var(--error-color)]">
                                          Not found in products — search
                                          another name or{" "}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              navigate("/add-product")
                                            }
                                            className="underline fw_600 text-[var(--primary)]"
                                          >
                                            add new product
                                          </button>
                                        </div>
                                      );
                                    return null;
                                  })()}
                                </>
                              );
                            })()}
                          </td>
                          <td
                            className={`${TD} min-w-[11rem] border-r border-r-EAECF0`}
                          >
                            <div
                              className={`rounded-md border ${
                                needsUom ? "border-F51D00" : "border-EAECF0"
                              }`}
                            >
                              <SelectField
                                handleSelected={(sel) => {
                                  setFieldValue(
                                    `itemData[${index}].item_uom`,
                                    sel?.label
                                  );
                                  setFieldValue(
                                    `itemData[${index}].is_item_uom_in_db`,
                                    true
                                  );
                                }}
                                borderclass=""
                                defaultSelectClass="border-0"
                                selectClass="w-full"
                                // Only a real system packing - not the vendor's
                                // pack text the server echoes on unmatched rows.
                                defaultValue={
                                  item?.item_uom_id && item?.item_uom
                                    ? { label: item.item_uom }
                                    : null
                                }
                                errMsgContainerClass="hidden"
                                id={`itemData[${index}].item_uom_id`}
                                isContainer={false}
                                options={uomOptions(item)}
                                name={`itemData[${index}].item_uom_id`}
                                placeholder={"Select packing"}
                              />
                            </div>
                          </td>

                          {/* Batch details */}
                          <td className={`${TD} whitespace-nowrap font-medium`}>
                            {show(item?.item_batch_number)}
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            {show(item?.item_exp_date)}
                          </td>
                          <td className={`${TD} ${NUM}`}>
                            {show(item?.item_sellable_quantity)}
                          </td>
                          <td className={`${TD} ${NUM}`}>
                            {show(item?.item_batch_free_quantity)}
                          </td>
                          <td className={`${TD} ${NUM}`}>
                            {show(item?.item_batch_purchase_rate)}
                          </td>
                          <td className={`${TD} ${NUM}`}>
                            {show(item?.item_batch_unit_price)}
                          </td>
                          <td className={`${TD} ${NUM}`}>
                            {show(gstOf(item).gst)}
                          </td>
                          <td className={`${TD} ${NUM}`}>
                            {show(item?.item_batch_discount_percentage)}
                          </td>

                          <td className={`${TD} w-[2.5rem]`}>
                            <button
                              type="button"
                              title="Remove this row from the import"
                              onClick={() => removeRow(index)}
                              className="p-1.5 rounded-md text-[var(--description-color)] hover:text-[var(--remove-color)] hover:bg-[var(--import-review-remove-hover-bg)]"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {visible.length === 0 && (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          className="py-10 text-center text-sm text-[var(--description-color)]"
                        >
                          {rows.length === 0
                            ? "No rows to import."
                            : "No rows need attention."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex flex-wrap justify-between items-center gap-3 w-full px-4 py-3 border-t border-EAECF0 bg-white">
                <span className="text-xs text-[var(--description-color)]">
                  {issueCount > 0 ? (
                    <>
                      <span className="font-semibold text-[var(--error-color)]">
                        {issueCount} row{issueCount > 1 ? "s" : ""}
                      </span>{" "}
                      still need a linked product or packing. Fix or remove
                      them to import.
                    </>
                  ) : (
                    "All rows are linked and ready to import."
                  )}
                </span>
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="h-10 px-5 rounded-lg border border-D0D5DD bg-white text-sm font-medium text-[var(--heading-color)] hover:bg-f3f8ff"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={rows.length === 0 || issueCount > 0}
                    className="h-10 px-5 rounded-lg text-sm font-semibold text-[var(--primary-button-fg-color)] bg-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Import {rows.length} item{rows.length === 1 ? "" : "s"}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      <ConfirmModal
        isOpen={!!mismatchInfo}
        title="Total amount doesn't match"
        content={
          mismatchInfo &&
          (() => {
            const fileTotal = Number(mismatchInfo.importedTotalAmount) || 0;
            const systemTotal =
              Number(mismatchInfo.systemCalculatedTotal) || 0;
            const diff = fileTotal - systemTotal;
            const row = "flex items-center justify-between gap-4 px-4 py-3";
            return (
              <div className="flex flex-col gap-3 text-sm">
                <p className="text-[var(--description-color)]">
                  The vendor's total differs from what the system calculated
                  from the item rates, quantities and taxes.
                </p>
                <div className="rounded-xl border border-[var(--light-border-color)] overflow-hidden">
                  <div className={row}>
                    <div>
                      <div className="text-[var(--heading-color)]">
                        Vendor file total
                      </div>
                      {mismatchInfo.columnName && (
                        <div className="text-[11px] text-[var(--description-color)]">
                          from column "{String(mismatchInfo.columnName).trim()}"
                        </div>
                      )}
                    </div>
                    <span className="fw_600 tabular-nums text-[var(--heading-color)]">
                      {rupees(fileTotal)}
                    </span>
                  </div>
                  <div
                    className={`${row} border-t border-[var(--light-border-color)] bg-[var(--row-hover-color)]`}
                  >
                    <span className="text-[var(--heading-color)]">
                      System calculated
                    </span>
                    <span className="fw_600 tabular-nums text-[var(--primary)]">
                      {rupees(systemTotal)}
                    </span>
                  </div>
                  <div
                    className={`${row} border-t border-[var(--light-border-color)]`}
                  >
                    <span className="text-[var(--heading-color)]">
                      Difference
                    </span>
                    <span className="fw_600 tabular-nums text-[var(--error-color)]">
                      {rupees(Math.abs(diff))}{" "}
                      <span className="text-[11px] fw_500">
                        {diff > 0 ? "more in file" : "less in file"}
                      </span>
                    </span>
                  </div>
                </div>
                <p className="text-xs text-[var(--description-color)]">
                  Continue to import with the system calculated amount, or go
                  back to check rates, quantities and GST.
                </p>
              </div>
            );
          })()
        }
        confirmText="Use system amount"
        cancelText="Go back"
        onConfirm={() => {
          const onProceed = mismatchInfo?.onProceed;
          setMismatchInfo(null);
          onProceed?.();
        }}
        onCancel={() => setMismatchInfo(null)}
      />
    </InnerLayout>
  );
};

export default GoodsOrderCsv;

// What still blocks a row from importing (same checks handleSubmit enforces).
const rowIssues = (item) => {
  const list = [];
  if (!item?.item_id) list.push("Link a product");
  if (!item?.item_uom_id) list.push("Select packing");
  return list;
};

// Packing options for a row. The resolved item_uom_id may belong to a
// different UOM group than this item's uom_dropdown, so it won't be in the
// options and the select would show the raw id - make sure the current
// selection is always present (labelled with its UOM name).
const uomOptions = (item) => {
  const opts =
    item?.uom_dropdown?.map((opt) => ({
      label: opt?.alt_uom,
      value: opt?.item_id,
    })) || [];
  if (
    item?.item_uom_id &&
    !opts.some((opt) => opt.value === item?.item_uom_id)
  ) {
    opts.unshift({ label: item?.item_uom, value: item?.item_uom_id });
  }
  return opts;
};

// Read a vendor-file cell by its mapped header. CSV headers often differ from
// the Import Template mapping only in case / spacing ("ITEM CODE" vs
// "Item Code") or by Excel's invisible BOM on the first header, so fall back
// to a BOM-, case- and whitespace-insensitive match.
const headerKey = (h) =>
  String(h ?? "")
    .replace(/^﻿/, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
const cell = (item, column) => {
  if (!item || !column) return undefined;
  if (item[column] !== undefined) return item[column];
  const want = headerKey(column);
  const key = Object.keys(item).find((k) => headerKey(k) === want);
  return key === undefined ? undefined : item[key];
};

// Compare packs loosely: case, spaces and quote marks don't matter, so
// "10'S", "10`s", "10 S" and "10s" are all the same pack.
// ---- product lookup (same helpers as the Stock IN "Import PDF") -----------
const searchProducts = async (value) => {
  if (!value) return [];
  const res = await purchaseProductListApi({ field: "item_name", value });
  return Array.isArray(res?.data?.data) ? res.data.data : [];
};

const fetchProductDetails = async (product) => {
  const res = await purchaseProductDetailsApi({
    item_id: product?.item_id,
    item_uom: product?.item_uom || "",
  });
  return res?.data?.data ?? null;
};

// The packing a linked row gets: the product's purchasing UOM, else its first.
const resolveUom = (product, details) => {
  const rawUomList = details?.uom_dropdown ?? [];
  const matchedUom =
    rawUomList.find(
      (uom) => uom?.item_id === product?.item_pur_purchasing_uom_name_id
    ) ??
    rawUomList.find(
      (uom) => uom?.alt_uom === product?.item_pur_purchasing_uom_name
    ) ??
    rawUomList[0];
  return { rawUomList, matchedUom };
};

const toProductOption = (product) => ({
  value: product?.item_id,
  label: product?.item_name,
  product,
});

// Run async jobs a few at a time so a long file doesn't flood the server.
const runLimited = async (jobs, limit = 4) => {
  let next = 0;
  const worker = async () => {
    while (next < jobs.length) {
      const job = jobs[next++];
      try {
        await job();
      } catch (error) {}
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, jobs.length) }, worker)
  );
};

const productSelectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 6,
    fontSize: 14,
    borderColor: state.selectProps.hasError
      ? "var(--error-color)"
      : state.isFocused
      ? "var(--primary)"
      : "var(--light-border-color)",
    boxShadow: "none",
    "&:hover": { borderColor: "var(--primary)" },
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 13,
    color: "var(--description-color)",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    color: "var(--heading-color)",
    backgroundColor: state.isFocused
      ? "var(--row-hover-color)"
      : "var(--tertiary-color)",
  }),
};

// Product names compare loosely: "CILNY M 25MG TAB" == "Cilny M 25mg Tab".
const normalizeName = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normalizeUom = (u) =>
  String(u ?? "")
    .toLowerCase()
    .replace(/[\s'`’"*.]/g, "");

// The product is linked but the server didn't resolve its packing: pick the
// option whose name matches the vendor's pack, ignoring case and punctuation.
const autoMatchPacking = (item, vendorUom) => {
  if (!item?.item_id || item?.item_uom_id) return item;
  const want = normalizeUom(vendorUom || item?.vendor_uom || item?.item_uom);
  if (!want) return item;
  const match = (item?.uom_dropdown || []).find(
    (opt) => normalizeUom(opt?.alt_uom) === want
  );
  if (!match) return item;
  return {
    ...item,
    item_uom: match.alt_uom,
    item_uom_id: match.item_id,
    is_item_uom_in_db: true,
  };
};

// GST % of a row, whichever way the vendor's Import Template maps it:
// - "GST" format             -> item_gst
// - "CGST/SGST" format       -> item_cgst_percentage + item_sgst_percentage
// - optional batch columns   -> item_batch_cgst/sgst/igst_percentage
// The screen and the import payload both read it from here, so what the user
// reviews is exactly what gets sent.
const pct = (v) => parseFloat(v) || 0;
const gstOf = (item) => {
  const cgst =
    pct(item?.item_batch_cgst_percentage) || pct(item?.item_cgst_percentage);
  const sgst =
    pct(item?.item_batch_sgst_percentage) || pct(item?.item_sgst_percentage);
  const igst =
    pct(item?.item_batch_igst_percentage) || pct(item?.item_igst_percentage);
  const gst = pct(item?.item_gst) || cgst + sgst || igst;
  return { gst, cgst, sgst, igst };
};

const rupees = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const show = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "—" : v;

// Colours come from the app's CSS variables / theme classes - the Tailwind
// theme replaces the default palette.
const TH =
  "h-[2.75rem] px-3 whitespace-nowrap text-xs fw_700 uppercase tracking-wide text-475467 bg-F2F4F5 border-b border-b-EAECF0";
const GROUP_TH =
  "h-[2rem] px-3 text-left text-[11px] fw_600 uppercase tracking-wider text-475467 bg-F2F4F5 opacity-80";
const TD = "text-sm h-[3.5rem] px-3 fw_400 text-secondary align-middle";
const NUM = "text-right tabular-nums whitespace-nowrap";
const CHIP =
  "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs font-semibold";
const ERROR_CHIP =
  "text-[var(--error-color)] bg-[var(--import-review-error-chip-bg)] border-[var(--import-review-error-chip-border)] cursor-pointer";

const COLUMNS = [
  { label: "" },
  { label: "#" },
  { label: "Code" },
  { label: "Product name" },
  { label: "Pack" },
  { label: "Code", className: "border-l border-l-EAECF0" },
  { label: "Linked product" },
  { label: "Packing", className: "border-r border-r-EAECF0" },
  { label: "Batch" },
  { label: "Expiry" },
  { label: "Qty", num: true },
  { label: "Free", num: true },
  { label: "Pur. price", num: true },
  { label: "MRP", num: true },
  { label: "GST %", num: true },
  { label: "Sch. disc %", num: true },
  { label: "" },
];
